import { Box, Button, Divider } from "@mui/material";
import ColorPicker from "components/ColorPicker";
import ContentShell from "components/ContentShell";
import FilePicker from "components/FilePicker";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextareaInput } from "components/FormTextArea";
import { SimpleTextInput } from "components/FormTextInput";
import SimpleSwitch from "components/Switch";
import TranslationButtons from "components/TranslationButtons";
import configData from "config/config.json";
import useOpenClose from "hooks/useOpenClose";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import sessionHelperInstance from "services/sessionHelper";
import {
  createStoreBanner,
  getStoreBannerInfo,
  updateStoreBanner,
} from "services/storeBannerService";
import { uploadImageV2 } from "services/uploadService";
import useStyles from "styles/store/storeEditor";
import { delay } from "utils/miscUtils";
import { storeBannerValidation } from "utils/validation";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";

interface StoreBannerDetails {
  order: number;
  title: string;
  titleTrans: Record<string, any>;
  body: string;
  bodyTrans: Record<string, any>;
  textColor: string;
  isActive: boolean;
  isTitleBold: boolean;
  isBodyBold: boolean;
  territory: string;
  imageUrl?: string;
}

const _storeBannerDetails: StoreBannerDetails = {
  order: 1,
  title: "",
  titleTrans: {},
  body: "",
  bodyTrans: {},
  textColor: "#000000",
  isActive: true,
  isTitleBold: false,
  isBodyBold: false,
  territory: "all",
};

const StoreBannerEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [storeBannerDetails, setStoreBannerDetails] =
    useState(_storeBannerDetails);

  const [modalData, setModalData] = useState({
    modal: "",
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [image, setImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);

  const { open, isOpen, close } = useOpenClose();

  const cancel = () => navigate(configData.routes.store.store);

  const submit = async () => {
    setLoading(true);
    try {
      let response;
      await storeBannerValidation.validate(storeBannerDetails);

      if (id === "create") {
        const { order } = storeBannerDetails;

        response = await createStoreBanner({
          ...storeBannerDetails,
          order: Number(order),
        });
      } else {
        const { _id, id, createdAt, updatedAt, imageUrl, ...rest } =
          storeBannerDetails as any;

        response = await updateStoreBanner(
          { ...rest, order: Number(rest.order) },
          _id
        );
      }

      if (image && response?.data?.fileUploadUrl) {
        const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.STORE_BANNER];
        await uploadImageV2({
          data: image,
          uploadUrl: response.data.fileUploadUrl,
          resize: true,
          resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
          resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
        });
      }

      setLoading(false);
      showSnackbar(response?.message ?? "Store updated successfully");
      delay(100);
      navigate(configData.routes.storeBanners.storeBanners);
    } catch (error) {
      setLoading(false);
      if (error.inner)
        error.inner.forEach((error) => showSnackbar(error?.message, "error"));
      showSnackbar(error?.message, "error");
    }
  };

  const fetchStoreBannerInfo = async () => {
    try {
      const resp = await getStoreBannerInfo(id);

      if (resp?.data) {
        let titleTrans = { ...resp.data?.titleTrans };
        let bodyTrans = { ...resp.data?.bodyTrans };

        if (resp?.data?.body && !bodyTrans["en"]) {
          bodyTrans["en"] = resp.data?.body;
        } else resp.data.body = bodyTrans["en"];
        if (resp?.data?.title && !titleTrans["en"]) {
          titleTrans["en"] = resp.data?.title;
        } else resp.data.title = titleTrans["en"];

        setStoreBannerDetails({ ...resp.data, bodyTrans, titleTrans });
      }
    } catch (error) {
      showSnackbar(error.message ?? "Failed to fetch store info", "error");
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e.target;
    setStoreBannerDetails((st) => ({
      ...st,
      [name]: ["isTitleBold", "isBodyBold", "isActive"].includes(name)
        ? !st[name]
        : value,
    }));
  };

  const handleModalChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getTranslatedData = (field: any) => {
    const _translatedData = Object.entries(storeBannerDetails?.[field]).map(
      ([key, value]) => {
        return {
          lang: key,
          inputValue: value,
        };
      }
    );
    if (!_translatedData) return [];
    return _translatedData;
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setImage(image ? image : null);
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setStoreBannerDetails((state) => ({
      ...state,
      fileType: "image/webp",
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error"
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setStoreBannerDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {}
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setStoreBannerDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setStoreBannerDetails((details) => ({
        ...details,
        [field?.split("Trans")[0]]: modalData.inputValue,
        [field]: { ...details[field], en: modalData.inputValue },
      }));
    }

    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  useEffect(() => {
    if (id !== "create") fetchStoreBannerInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"}  Store Banner`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box>
        <Box className={styles.container}>
          <Box style={{ display: "flex", flexFlow: "row wrap", gap: 10 }}>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Title"
                name="title"
                value={storeBannerDetails.title}
                onChange={onChange}
                button={
                  <TranslationButtons
                    field={"titleTrans"}
                    onClick={handleOpenModal}
                  />
                }
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Order"
                name="order"
                type="number"
                value={storeBannerDetails.order}
                onChange={onChange}
              />
            </Box>
          </Box>
          <Box
            style={{
              display: "flex",
              flexFlow: "row wrap",
              gap: 8,
              alignItems: "flex-end",
            }}
          >
            <Box className={styles.field}>
              <SimpleTextareaInput
                label="Body"
                name="body"
                value={storeBannerDetails.body}
                onChange={onChange}
                buttons={
                  <TranslationButtons
                    field={"bodyTrans"}
                    onClick={handleOpenModal}
                  />
                }
              />
            </Box>
            <Box className={styles.field}>
              <SimpleSelectInput
                label="Territory"
                name="territory"
                value={storeBannerDetails?.territory || ""}
                options={sessionHelperInstance.countryOptions}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <ColorPicker
                label={"Text Color"}
                name="textColor"
                value={storeBannerDetails.textColor}
                onChange={onChange}
                customStyle={{ width: "100%", marginTop: 20 }}
              />
            </Box>
            <Box className={styles.field} style={{ alignSelf: "center" }}>
              <SimpleSwitch
                legendText={"Title Style (Bold/Normal)"}
                name="isTitleBold"
                value={storeBannerDetails.isTitleBold}
                label={storeBannerDetails.isTitleBold ? "Bold" : "Normal"}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field} style={{ alignSelf: "center" }}>
              <SimpleSwitch
                legendText={"Body Style (Bold/Normal)"}
                name="isBodyBold"
                value={storeBannerDetails.isBodyBold}
                label={storeBannerDetails.isBodyBold ? "Bold" : "Normal"}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field} style={{ alignSelf: "center" }}>
              <SimpleSwitch
                legendText={"Banner Status"}
                name="isActive"
                value={storeBannerDetails.isActive}
                label={storeBannerDetails.isActive ? "Active" : "Inactive"}
                onChange={onChange}
              />
            </Box>
          </Box>
          <Box className={styles.imagePickerContainer2}>
            <Divider style={{ marginTop: 10 }} variant="fullWidth" />
            <Box className={styles.imagePickerAndPreviewContainer}>
              <FilePicker
                label={"Select Store Banner Image"}
                value={image}
                name={"storeBannerImage"}
                onFileSelect={onSelectImage}
                imageType={ImageType.STORE_BANNER}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.STORE_BANNER].width
                  } x ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.STORE_BANNER].height
                  } pixels. Images will be resized and converted to WebP.`,
                }}
              />
              {storeBannerDetails?.imageUrl && !image && (
                <Box
                  component="img"
                  src={storeBannerDetails?.imageUrl}
                  alt={`Banner Image Preview`}
                  style={{
                    aspectRatio: "16/9",
                    maxHeight: 200,
                    maxWidth: 400,
                    objectFit: "contain",
                    marginTop: 2,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <ViewLanguageModal
        isMultiline
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleModalChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />
    </ContentShell>
  );
};

export default StoreBannerEditor;
